import { useMemo } from "react";
import { ProgramPhaseList } from "./ProgramPhaseList";
import { ProgramPhase } from "@core/features/program";
import {
  useFetchActions,
  ONBOARDING_ACTION_TYPES,
  sortActionsByStatusAndDueDate,
} from "@core/features/actions";
import { usePlatformStore } from "@platform/store";

export type Props = {
  selectedPhase: ProgramPhase | undefined;
  setSelectedPhase: (phase: ProgramPhase) => void;
  phaseContainerRef: React.RefObject<HTMLDivElement>;
  onPhaseListReady: () => void;
  isLoading: boolean;
  phases?: ProgramPhase[];
  programId: number;
};

/**
 * Container for ProgramPhaseList.
 * Fetches user actions and merges them into phases.
 */
export function ProgramPhaseListContainer({
  selectedPhase,
  setSelectedPhase,
  phaseContainerRef,
  onPhaseListReady,
  isLoading,
  phases = [],
  programId,
}: Props) {
  const user = usePlatformStore((s) => s.user);
  const { data: actionsData } = useFetchActions({ programId, user });
  const userActions = actionsData?.actions;

  const phasesWithActions = useMemo(() => {
    if (!userActions) return phases;

    return phases.map((phase) => {
      const filteredActions = userActions.filter((action) => {
        // Filter to this phase
        if (String(action.program_phase) !== String(phase.id)) return false;
        // Exclude onboarding flow actions (they're handled by OnboardingGate)
        const slug = action.action_type?.slug ?? "";
        if (ONBOARDING_ACTION_TYPES.includes(slug)) return false;
        return true;
      });
      // Sort by status (completed last), then by due date
      const actions = sortActionsByStatusAndDueDate(filteredActions);
      return { ...phase, actions };
    });
  }, [phases, userActions]);

  if (phases.length === 0) {
    return null;
  }

  return (
    <ProgramPhaseList
      phases={phasesWithActions}
      selectedPhase={selectedPhase}
      setSelectedPhase={setSelectedPhase}
      phaseContainerRef={phaseContainerRef}
      onPhaseListReady={onPhaseListReady}
      isLoading={isLoading}
    />
  );
}

export default ProgramPhaseListContainer;

